import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
} from '@angular/core';
import {
  faFastBackward,
  faFastForward,
  faStepBackward,
  faStepForward,
} from '@fortawesome/free-solid-svg-icons';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ShapeState } from '../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { customIcon } from '../../../../../../cross-team-shared/icons/register-custom-icons';
import { KeyboardShortcutName } from '../../../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { AbstractAnnotationStore } from '../../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { TaskWorkspaceStore } from '../../../../../h3ml/state/task-workspace-store';
import {
  fastBackward,
  fastForward,
  skipBackward,
  skipForward,
  stepBackward,
  stepForward,
} from '../../../../../shared/icons/custom-icons';
import { RootKeyboardShortcutService } from '../../../../../shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { ObjectlessFocusedShapeService } from '../../utils/objectless-focused-shape.service';
import { OutputKeyboardShortcutService } from '../../utils/output-keyboard-shortcut.service';
import { RootFocusedShapeService } from '../../utils/root-focused-shape.service';
import { FocusableScrubberService } from './focusable-scrubber.service';
import { FocusedAreaRange } from './focusable-scrubber.utils';

@Component({
  selector: 'h3-focusable-scrubber',
  templateUrl: './focusable-scrubber.component.html',
  styleUrls: ['./focusable-scrubber.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FocusableScrubberService],
  standalone: false,
})
export class FocusableScrubberComponent extends DirectiveSuperclass {
  frameNumber$ = this.focusableScrubberService.frameNumber$;
  framesPerSecond = this.focusableScrubberService.framesPerSecond;
  focusedShapeStore$: Observable<Store<ShapeState> | undefined>;
  maxFrameNumber$ = this.focusableScrubberService.maxFrameNumber$;
  isPlaying$ = this.focusableScrubberService.isPlaying$;
  backwardTooltip =
    'Back one frame' +
    this.rootKeyboardShortcutService.tooltipSuffix('scrubBackward');
  forwardTooltip =
    'Forward one frame' +
    this.rootKeyboardShortcutService.tooltipSuffix('scrubForward');
  skipBackwardTooltip =
    'Back five frames' +
    this.rootKeyboardShortcutService.tooltipSuffix('skipScrubBackward');
  skipForwardTooltip =
    'Forward five frames' +
    this.rootKeyboardShortcutService.tooltipSuffix('skipScrubForward');
  smartSkipBackwardTooltip =
    'Previous important frame' +
    this.rootKeyboardShortcutService.tooltipSuffix('smartSkipBackward');
  smartSkipForwardTooltip =
    'Next important frame' +
    this.rootKeyboardShortcutService.tooltipSuffix('smartSkipForward');
  playTooltip =
    'Play' + this.rootKeyboardShortcutService.tooltipSuffix('playPause');
  pauseTooltip =
    'Pause' + this.rootKeyboardShortcutService.tooltipSuffix('playPause');
  stepBackward = faStepBackward;
  stepForward = faStepForward;
  fastBackward = faFastBackward;
  fastForward = faFastForward;
  skipBackward = customIcon(skipBackward);
  skipForward = customIcon(skipForward);
  isSmartSkipOn = false;
  focusedAreaRange$: Observable<FocusedAreaRange>;
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private shortcutService = this.taskWorkspaceStore.state().splitViewOutputName
    ? this.rootKeyboardShortcutService
    : this.outputKeyboardShortcutService;
  private focusedShapeService = inject(RootFocusedShapeService);
  private objectlessFocusedShapeService = inject(ObjectlessFocusedShapeService);

  constructor(
    private outputKeyboardShortcutService: OutputKeyboardShortcutService,
    private rootKeyboardShortcutService: RootKeyboardShortcutService,
    private store: AbstractAnnotationStore,
    injector: Injector,
    private focusableScrubberService: FocusableScrubberService,
  ) {
    super(injector);
    this.focusedShapeStore$ = this.taskWorkspaceStore.isUsingObjects()
      ? this.focusedShapeService.getFocusedShapeStore$()
      : this.objectlessFocusedShapeService.getFocusedShapeStore$();
    this.focusedAreaRange$ = this.store('focusedAreaRange').$.pipe(
      filter(isDefined),
    );
    this.registerKeyboardShortcuts();

    this.stepBackward = customIcon(stepBackward);
    this.stepForward = customIcon(stepForward);
    this.fastBackward = customIcon(fastBackward);
    this.fastForward = customIcon(fastForward);
    this.isSmartSkipOn = true;
  }

  playButtonPressed() {
    this.focusableScrubberService.play('button');
  }

  pauseButtonPressed() {
    this.focusableScrubberService.pause();
  }

  smartSkipForward() {
    this.focusableScrubberService.moveToClosestImportantFrame(true, {
      source: 'button',
    });
  }

  smartSkipBackward() {
    this.focusableScrubberService.moveToClosestImportantFrame(false, {
      source: 'button',
    });
  }

  moveToFrame(frameNumber: number) {
    this.focusableScrubberService.scrub(frameNumber, {
      source: 'input',
      method: 'frame_number_input',
    });
  }

  onSliderChange(frameNumber: number) {
    this.focusableScrubberService.scrub(frameNumber, {
      source: 'slider',
      method: 'slider',
    });
  }

  scrubWithButton(delta: number, method: string) {
    this.focusableScrubberService.scrubFromSource(delta, 'button', method);
  }

  // eslint-disable-next-line max-lines-per-function -- Lots of keyboard shortcuts
  private registerKeyboardShortcuts() {
    this.registerKeyboardShortcut('scrubBackward', -1);
    this.registerKeyboardShortcut('scrubForward', 1);
    this.registerKeyboardShortcut('skipScrubBackward', -5);
    this.registerKeyboardShortcut('skipScrubForward', 5);

    this.subscribeTo(
      this.shortcutService.getEvent$('smartSkipBackward'),
      ({ eventTrackingValue }) => {
        this.focusableScrubberService.moveToClosestImportantFrame(
          false,
          eventTrackingValue,
        );
      },
    );

    this.subscribeTo(
      this.shortcutService.getEvent$('smartSkipForward'),
      ({ eventTrackingValue }) => {
        this.focusableScrubberService.moveToClosestImportantFrame(
          true,
          eventTrackingValue,
        );
      },
    );

    if (this.framesPerSecond) {
      this.subscribeTo(this.shortcutService.getEvent$('playPause'), () => {
        if (this.isPlaying$.value) {
          this.focusableScrubberService.pause();
        } else {
          this.focusableScrubberService.play('keyboard_shortcut');
        }
      });
    }
  }

  private registerKeyboardShortcut(
    shortcutName: KeyboardShortcutName,
    frameNumberDelta: number,
  ) {
    this.subscribeTo(
      this.shortcutService.getEvent$(shortcutName),
      ({ eventTrackingValue }) => {
        this.focusableScrubberService.scrubByDelta(frameNumberDelta, {
          ...eventTrackingValue,
          ...this.getMethod(shortcutName),
        });
      },
    );
  }

  private getMethod(shortcutName: KeyboardShortcutName) {
    if (shortcutName === 'scrubBackward') {
      return { method: 'back' };
    }
    if (shortcutName === 'scrubForward') {
      return { method: 'forward' };
    }
    if (shortcutName === 'skipScrubBackward') {
      return { method: 'skip_backward' };
    }
    if (shortcutName === 'skipScrubForward') {
      return { method: 'skip_forward' };
    }
    return {};
  }
}
